/**
 * Filters modal for the Vehicle Inventory table. Multi-select for categorical
 * fields (rooftop, status, type, title status), inclusive ranges for numeric
 * ones (year, age, odometer). The draft is edited locally and only committed on
 * Apply; the page persists the applied filter as a user preference.
 */
import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

import { THEME } from "../../config/theme"
import { InventoryFilterOptions } from "../../api/inventory"

export interface FilterState {
  rooftop: string[]
  status: string[]
  vehicleType: string[]
  website: string[]
  yearMin: number | null
  yearMax: number | null
  ageMin: number | null
  ageMax: number | null
}

export const EMPTY_FILTER: FilterState = {
  rooftop: [],
  status: [],
  vehicleType: [],
  website: [],
  yearMin: null,
  yearMax: null,
  ageMin: null,
  ageMax: null,
}

// Number of active filter groups — drives the "Filters (N)" badge.
export function countActiveFilters(filter: FilterState): number {
  return (
    (filter.rooftop.length ? 1 : 0) +
    (filter.status.length ? 1 : 0) +
    (filter.vehicleType.length ? 1 : 0) +
    (filter.website.length ? 1 : 0) +
    (filter.yearMin != null || filter.yearMax != null ? 1 : 0) +
    (filter.ageMin != null || filter.ageMax != null ? 1 : 0)
  )
}

type CategoryKey = "rooftop" | "status" | "vehicleType" | "website"

interface Props {
  open: boolean
  options: InventoryFilterOptions | null
  initial: FilterState
  onApply: (filter: FilterState) => void
  onClose: () => void
}

export default function InventoryFiltersModal({ open, options, initial, onApply, onClose }: Props) {
  const [draft, setDraft] = useState<FilterState>(initial)

  // Re-seed the draft each time the modal opens (in case the applied filter changed).
  useEffect(() => {
    if (open) setDraft(initial)
  }, [open, initial])

  if (!open) return null

  const toggleCategory = (key: CategoryKey, value: string) => {
    setDraft((current) => {
      const selected = current[key]
      const next = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
      return { ...current, [key]: next }
    })
  }

  const setNumber = (key: keyof FilterState, raw: string) => {
    setDraft((current) => ({ ...current, [key]: raw === "" ? null : Number(raw) }))
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(event) => event.stopPropagation()}>
        <div style={panelHeaderStyle}>
          <span>Filters</span>
          <button style={closeButtonStyle} onClick={onClose} aria-label="Close filters">
            <X size={18} />
          </button>
        </div>

        <div style={bodyStyle}>
          <MultiSelect label="On Website" values={options?.website_presence ?? []} selected={draft.website} onToggle={(v) => toggleCategory("website", v)} />
          <Range label="Inventory age (days)" min={draft.ageMin} max={draft.ageMax} bounds={options?.age_days} onMin={(v) => setNumber("ageMin", v)} onMax={(v) => setNumber("ageMax", v)} />
          <MultiSelect label="Rooftop" values={options?.rooftops ?? []} selected={draft.rooftop} onToggle={(v) => toggleCategory("rooftop", v)} />
          <MultiSelect label="Type" values={options?.vehicle_types ?? []} selected={draft.vehicleType} onToggle={(v) => toggleCategory("vehicleType", v)} />
          <MultiSelect label="Status" values={options?.statuses ?? []} selected={draft.status} onToggle={(v) => toggleCategory("status", v)} />
          <Range label="Year" min={draft.yearMin} max={draft.yearMax} bounds={options?.year} onMin={(v) => setNumber("yearMin", v)} onMax={(v) => setNumber("yearMax", v)} />
        </div>

        <div style={footerStyle}>
          <button style={clearButtonStyle} onClick={() => setDraft(EMPTY_FILTER)}>
            Clear all
          </button>
          <div style={{ display: "flex", gap: THEME.spacing.sm }}>
            <button style={cancelButtonStyle} onClick={onClose}>Cancel</button>
            <button style={applyButtonStyle} onClick={() => onApply(draft)}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function MultiSelect({ label, values, selected, onToggle }: {
  label: string
  values: string[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <div style={sectionStyle}>
      <div style={sectionLabelStyle}>{label}</div>
      {values.length === 0 ? (
        <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>No options</div>
      ) : (
        <div style={checkboxListStyle}>
          {values.map((value) => (
            <label key={value} style={checkboxRowStyle}>
              <input type="checkbox" checked={selected.includes(value)} onChange={() => onToggle(value)} />
              <span>{value}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

function Range({ label, min, max, bounds, onMin, onMax }: {
  label: string
  min: number | null
  max: number | null
  bounds?: { min: number | null; max: number | null }
  onMin: (raw: string) => void
  onMax: (raw: string) => void
}) {
  return (
    <div style={sectionStyle}>
      <div style={sectionLabelStyle}>{label}</div>
      <div style={{ display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
        <input
          type="number"
          value={min ?? ""}
          placeholder={bounds?.min != null ? `min ${bounds.min}` : "min"}
          onChange={(event) => onMin(event.target.value)}
          style={rangeInputStyle}
        />
        <span style={{ color: THEME.colors.neutral.light.textSecondary }}>–</span>
        <input
          type="number"
          value={max ?? ""}
          placeholder={bounds?.max != null ? `max ${bounds.max}` : "max"}
          onChange={(event) => onMax(event.target.value)}
          style={rangeInputStyle}
        />
      </div>
    </div>
  )
}

// --- Styles ---

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const panelStyle: React.CSSProperties = {
  background: THEME.colors.white,
  borderRadius: THEME.borderRadius.md,
  width: 480,
  maxWidth: "90vw",
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
  fontFamily: THEME.fonts.primary,
}

const panelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${THEME.spacing.md} ${THEME.spacing.lg}`,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  fontSize: THEME.typography.h4.size,
  fontWeight: THEME.typography.h4.weight,
  color: THEME.colors.text,
}

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  display: "flex",
  padding: 0,
}

const bodyStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  overflowY: "auto",
}

const sectionStyle: React.CSSProperties = {
  marginBottom: THEME.spacing.md,
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: THEME.spacing.xs,
}

const checkboxListStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  maxHeight: 140,
  overflowY: "auto",
}

const checkboxRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  fontSize: 13,
  color: THEME.colors.text,
  cursor: "pointer",
}

const rangeInputStyle: React.CSSProperties = {
  flex: 1,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 13,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.border}`,
  fontFamily: THEME.fonts.primary,
  minWidth: 0,
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${THEME.spacing.md} ${THEME.spacing.lg}`,
  borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
}

const clearButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: THEME.colors.neutral.light.textSecondary,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const cancelButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.border}`,
  background: THEME.colors.white,
  color: THEME.colors.text,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const applyButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  background: THEME.colors.navy,
  color: THEME.colors.white,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
